/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { takeUntil } from 'rxjs/operators';
import { dateCache } from '@touchplan/common';
import { Rectangle } from 'pixi.js-legacy';
import { DestroyOptions, IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { cssVar } from '@touchplan/css-var';
import { AbstractDisplayLine } from '@appPlanApplication/timeline/AbstractDisplayLine';
import { ACTIVE_LINE_LABEL_TEXT } from '@touchplan/strings';
import { applyDisplayLineTextStyling } from '@appPlanApplication/timeline/TimelineHelpers';
import { ActiveLineTap } from '@appPlanApplication/timeline/active-line/ActiveLineTap';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { ActiveLineDrag } from '@appPlanApplication/timeline/active-line/ActiveLineDrag';
import { GhostLineDrawer } from '@appPlanApplication/timeline/ghost-line/GhostLineDrawer';

/**
 * Visual configuration settings for the active line.
 * These settings control the appearance of both the line and its label.
 * The hitArea settings control how much of an area around the visible line can be interacted with.
 * This allows for less precise gestures and interaction with smaller objects (like using a finger to interact with a thin line)
 */
const settings = {
	lineColor: cssVar.almostBlack, // Active Line specific overrides
	lineWidth: 20, // Active Line specific overrides
	labelColor: `#ffffff`, // Active Line label color override
	labelFormat: `MMM DD YYYY`,
	hitAreaOffset: 10, // The offset from origin to start interaction detection (10 = will start 10px to the left of the visible line)
	hitAreaWidth: 40, // The width of the interaction area. Offset of 10 and 40 width means there is a 10px "grace" area on each side
};
/**
 * ActiveLineDrawer is responsible for rendering the active line indicator in the canvas.
 * The active line shows the current date with a vertical line and text label.
 */
export class ActiveLineDrawer extends AbstractDisplayLine {
	/**
	 * We store the last ActiveLine as a cache to handle cases where the canvas is forced to redraw
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * Component manager instance which holds logic for interaction gestures
	 * @private
	 */
	private _components: ComponentManager;
	/**
	 * The Ghost Line Drawer is a AbstractDisplayLine that represents the ghost line when a drag operation starts.
	 * @private
	 */
	private _ghostLine: GhostLineDrawer;
	/**
	 * Creates a new ActiveLineDrawer instance.
	 * @param _canvasAngularLand - Interface to Angular services/dependencies
	 * @param _application - The canvas application instance
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, _application: AbstractApplication) {
		super(_application, LayerPaths.BACKGROUND_TIMELINE_ACTIVE_LINE);
		this.setup();
		this.setupSubscribers();
	}
	/**
	 * Updates the active line's position based on camera transforms.
	 * This ensures the line stays correctly positioned during pan/zoom operations.
	 * @param _ - Delta Time elapsed since last update
	 */
	public update(_?: number): void {
		if (!this._container || !this._cachedActiveLine) {
			return;
		}
		const data: ActiveLine = this._cachedActiveLine;
		// Y-value of 0 to start at the top of the screen
		super.update(this.parentApplication.camera.realToScreenX(data.view.activeLineX), 0);
		this.updateInteractionArea();
		this._ghostLine.update();
	}

	/**
	 * UpdateInteractionArea
	 * Sets the interaction area to be slightly larger than the visual display line
	 * to make gestures easier on certain devices (like touchscreens)
	 * @private
	 */
	private updateInteractionArea(): void {
		// Using 0 because we want hit area to start at origin on the y-axis
		this._container.hitArea = new Rectangle(
			-settings.hitAreaOffset,
			0,
			settings.hitAreaWidth,
			this.parentApplication.camera.getScreen().height
		);
	}
	/**
	 * Draws the active line visualization.
	 * Creates a vertical line and positions the rotated date label.
	 *
	 * In order to hide the activeLine, you must either explicitly pass in `null` as a parameter
	 * or force set `ActiveLine.hasActiveSide` to force through the ActiveLineService. In the case of the latter,
	 * it will override any parameter passed into this method.
	 * @param activeLine - Active line data to render, defaults to cached data
	 */
	public draw(activeLine: ActiveLine | null = this._cachedActiveLine): void {
		if (!activeLine || !activeLine?.view.hasActiveSide) {
			this.hide();
			return;
		}
		this._graphics.clear();
		this._container.visible = true;

		this._label.text = `${ACTIVE_LINE_LABEL_TEXT} ${dateCache.getFormat(
			activeLine.view.activeLineDate,
			settings.labelFormat
		)}`;
		// Active Line overrides the base draw because of different styles
		const height: number = this.parentApplication.camera.getScreen().height;
		this._graphics.beginFill(settings.lineColor);
		this._graphics.drawRect(0, 0, settings.lineWidth, height);
		this._graphics.endFill();
		applyDisplayLineTextStyling(this._label);
		this._label.style.fill = settings.labelColor;
		// Add the header height to the label Y to offset it from the top of the screen
		this._label.y = this._label.y + cssVar.headerHeight_px;

		this._ghostLine.draw();

		this._cachedActiveLine = activeLine;
		// We are updating the position of the visual in update(). Since draw() can cause the position of the line
		// to change, we need to explicitly invoke update to ensure the update is positioned correctly.
		// If we don't do this, then the line will be out of position until the next tick (any type of interaction with the canvas)
		this.update();
		this.parentApplication.markAsDirty();
	}
	/**
	 * Cleans up resources when the drawer is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		super.destroy(opts);
		// Ghost Line is sourceOnly otherwise textures and shaders will be destroyed multiple times and throw an error
		this._ghostLine.destroy(DestroyOptions.sourceOnly);
		this._components = null;
		this._ghostLine = null;
		this._canvasAngularLand = null;
	}
	/**
	 * Sets up the layer structure for the milestone line.
	 * Creates a container and adds the line and label as children.
	 */
	private setup(): void {
		this._ghostLine = new GhostLineDrawer(
			this._canvasAngularLand,
			this.parentApplication,
			LayerPaths.BACKGROUND_TIMELINE_ACTIVE_LINE_GHOST
		);

		// Swap the order the Active Line and Ghost Line appear in the scene graph. This then allows the ghost line
		// to draw under the active line
		this._container?.parent?.swapChildren(this._container, this._ghostLine.container);

		this._components = new ComponentManager(this._container);
		this._components.add(
			`activeLineTapGesture`,
			new ActiveLineTap(),
			this._canvasAngularLand,
			this._container
		);

		this._components.add(
			`activeLineDragGesture`, // Unique identifier of the component, doesn't do anything and is mainly used for debugging
			new ActiveLineDrag(),
			this._canvasAngularLand,
			this._container,
			this._ghostLine,
			this._label
		);

		this.destroyList.push(this._components);
	}
	/**
	 * Sets up subscription to today line updates from the service.
	 * Triggers redraws when the today line data changes.
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((activeLine: ActiveLine) => this.draw(activeLine));
	}
}
